import { TranslateService } from '@ngstack/translate';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

const LNG_KEY = 'SELECTED_LANGUAGE';

/**
 * Service for managing application language and translations.
 * Wrapper around @ngstack/translate to make refactoring easier if we decide to swap the translation library.
 * @see {@link https://www.npmjs.com/package/@ngstack/translate}
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  public static readonly DEFAULT_APP_LANGUAGE = 'en';
  private supportedLangs: string[] = ['en', 'fr'];
  private _activeLang: string = LanguageService.DEFAULT_APP_LANGUAGE;
  private readonly activeLangSubject = new BehaviorSubject<string>(LanguageService.DEFAULT_APP_LANGUAGE);

  get activeLang$(): Observable<string> {
    return this.activeLangSubject.asObservable();
  }

  get activeLang(): string {
    return this.translate.activeLang || this._activeLang;
  }

  set activeLang(value: string) {
    this._activeLang = value;
    this.activeLangSubject.next(value);
  }

  constructor(private translate: TranslateService) {
    this.initAppLang();
  }

  /**
   * Initialize the application language from localStorage or browser locale
   */
  private initAppLang(): void {
    const storedLang = localStorage.getItem(LNG_KEY);
    
    if (storedLang && this.supportedLangs.includes(storedLang)) {
      this.translate.activeLang = storedLang;
      this._activeLang = storedLang;
      this.activeLangSubject.next(storedLang);
    } else {
      const browserLang = this.getBrowserLang();
      if (browserLang && this.supportedLangs.includes(browserLang)) {
        this.translate.activeLang = browserLang;
        this._activeLang = browserLang;
        this.activeLangSubject.next(browserLang);
      } else {
        this.translate.activeLang = LanguageService.DEFAULT_APP_LANGUAGE;
        this._activeLang = LanguageService.DEFAULT_APP_LANGUAGE;
        this.activeLangSubject.next(LanguageService.DEFAULT_APP_LANGUAGE);
      }
      localStorage.setItem(LNG_KEY, this._activeLang);
    }
  }

  /**
   * Set the application language
   * @param lang Language code (e.g., 'en', 'fr')
   */
  setLanguage(lang: string): void {
    if (this.supportedLangs.includes(lang)) {
      this.translate.activeLang = lang;
      this.translate.use(lang);
      this._activeLang = lang;
      this.activeLangSubject.next(lang);
      localStorage.setItem(LNG_KEY, lang);
    }
  }

  /**
   * Get list of supported languages
   */
  getLanguages(): Array<{ text: string; value: string }> {
    return [
      { text: 'English', value: 'en' },
      { text: 'Français', value: 'fr' }
    ];
  }

  /**
   * Get supported language codes
   */
  getSupportedLanguages(): string[] {
    return [...this.supportedLangs];
  }

  /**
   * Returns the language code name from the browser, e.g. "en" or "fr"
   * @returns string | undefined
   */
  public getBrowserLang(): string | undefined {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
      return undefined;
    }

    let browserLang: any = window.navigator.languages ? window.navigator.languages[0] : null;
    browserLang = browserLang || window.navigator.language;

    if (browserLang && browserLang.indexOf('-') !== -1) {
      browserLang = browserLang.split('-')[0];
    }

    if (browserLang && browserLang.indexOf('_') !== -1) {
      browserLang = browserLang.split('_')[0];
    }

    return browserLang;
  }

  /**
   * Returns the culture language code name from the browser, e.g. "en-US" or "fr-FR"
   * @returns string | undefined
   */
  public getBrowserCultureLang(): string | undefined {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
      return undefined;
    }
    let browserCultureLang: any = window.navigator.languages ? window.navigator.languages[0] : null;
    browserCultureLang = browserCultureLang || window.navigator.language;

    return browserCultureLang;
  }

  /**
   * Get translation for a key
   * @param key Translation key
   * @returns Translated string
   */
  public get(key: string): string {
    return this.translate.get(key);
  }
}
